import React, { useEffect } from 'react';
import { FilePickerDocumentList } from './file-picker-document-list';
import { SelectedCase } from './file-picker';
import { useLazyQuery } from '@apollo/client';
import { ComponentQueryLoader } from '../../../../../shared/component-query-loader';
import { SelectedFileInfo } from './verification-status-report-document-picker';
import {
  CasesSendReportFilePickerDocumentListContainerArchivedCredentialVerificationCaseSummaryDocument,
  CasesSendReportFilePickerDocumentListContainerArchivedIdentityVerificationCaseSummaryDocument,
  CasesSendReportFilePickerDocumentListContainerArchivedSendReportCaseSummaryDocument,
  CasesSendReportV1FilePickerDocumentListContainerCredentialVerificationCaseV1Document,
  CasesSendReportV1FilePickerDocumentListContainerIdentityVerificationCaseV1Document,
  CasesSendReportV1FilePickerDocumentListContainerSendReportCaseV1Document
} from '../../../../../../generated';

interface FilePickerDocumentListContainerProps {
  selectedCaseInfo: SelectedCase;
  handleFileSelection: (fileInfo: SelectedFileInfo) => void;
  isCoverLetter?: boolean;
}

export const FilePickerDocumentListContainer: React.FC<FilePickerDocumentListContainerProps> = ({ selectedCaseInfo, handleFileSelection, isCoverLetter }) => {
  const [caseData, setCaseData] = React.useState<any>(null);
  const [caseLoading, setCaseLoading] = React.useState<boolean>(false);
  const [caseError, setCaseError] = React.useState<any>(null);

  const filterCaseData = (caseData: any) => {
    const formattedData = caseData.files.filter((file: any) => {
      if (isCoverLetter) {
        return file.name.endsWith('photo.jpg');
      } else {
        return file.name.endsWith('.pdf');
      }
    });
    setCaseData({ ...caseData, files: formattedData });
  };

  const [identityQuery] = useLazyQuery(CasesSendReportV1FilePickerDocumentListContainerIdentityVerificationCaseV1Document);
  const [credentialQuery] = useLazyQuery(CasesSendReportV1FilePickerDocumentListContainerCredentialVerificationCaseV1Document);
  const [sendCaseQuery] = useLazyQuery(CasesSendReportV1FilePickerDocumentListContainerSendReportCaseV1Document);
  const [archivedCredentialQuery] = useLazyQuery(CasesSendReportFilePickerDocumentListContainerArchivedCredentialVerificationCaseSummaryDocument);
  const [archivedIdentityQuery] = useLazyQuery(CasesSendReportFilePickerDocumentListContainerArchivedIdentityVerificationCaseSummaryDocument);
  const [archivedSendReportQuery] = useLazyQuery(CasesSendReportFilePickerDocumentListContainerArchivedSendReportCaseSummaryDocument);

  const fetchCaseData = async (query: any, variables: any) => {
    try {
      const { data, loading, error } = await query({ variables });
      const caseDataKey = Object.keys(data)[0]; // Get the first key of the returned data object
      const caseDataTemp = data[caseDataKey];
      if (caseDataTemp?.files) {
        filterCaseData(caseDataTemp);
      }
      setCaseLoading(loading);
      setCaseError(error);
    } catch (error) {
      console.error('Error fetching case data:', error);
      setCaseLoading(false);
      setCaseError(error);
    }
  };

  useEffect(() => {
    if (!selectedCaseInfo || !selectedCaseInfo.id) {
      filterCaseData({ files: [] });
      setCaseLoading(false);
      setCaseError(null);
      return;
    }

    const { caseType, isArchived, id } = selectedCaseInfo;

    if (caseType === 'id-verification') {
      if (isArchived) {
        fetchCaseData(archivedIdentityQuery, { archivedIdentityVerificationCaseSummaryId: id });
      } else {
        fetchCaseData(identityQuery, { identityVerificationCaseV1Id: id });
      }
    } else if (caseType === 'credential-verification') {
      if (isArchived) {
        fetchCaseData(archivedCredentialQuery, { archivedCredentialVerificationCaseSummaryId: id });
      } else {
        fetchCaseData(credentialQuery, { credentialVerificationCaseV1Id: id });
      }
    } else if (caseType === 'send-report') {
      if (isArchived) {
        fetchCaseData(archivedSendReportQuery, { archivedSendReportCaseSummaryId: id });
      } else {
        fetchCaseData(sendCaseQuery, { sendReportCaseV1Id: id });
      }
    }
  }, [selectedCaseInfo]);

  return (
    <ComponentQueryLoader
      loading={caseLoading}
      hasData={caseData !== null}
      error={caseError}
      hasDataComponent={<FilePickerDocumentList caseData={caseData} handleFileSelection={handleFileSelection} caseType={selectedCaseInfo.caseType} />}
      noDataComponent={<></>}
    />
  );
};